import { useAuth } from '@/lib/auth'
import { LangToggle } from '@/lib/i18n/LangToggle'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { t } from '@/lib/i18n'

export const Route = createFileRoute('/login')({
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>) => ({
    next: typeof search.next === 'string' ? search.next : undefined,
  }),
})

function LoginPage() {
  const { status, login } = useAuth()
  const navigate = useNavigate()
  const { next } = Route.useSearch()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (status === 'authed') {
      navigate({ to: next ?? '/', replace: true })
    }
  }, [status, next, navigate])

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await login(email.trim(), password)
      navigate({ to: next ?? '/', replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Login failed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground px-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm bg-card border border-border rounded-lg p-6 space-y-4"
      >
        <div className="flex justify-end -mt-2 -mr-2">
          <LangToggle />
        </div>
        <div className="flex justify-center pb-1">
          {/* WP2A6A: the full zonoo.ai signature belongs on login; it
              respects the ambient light/dark class the same way the rest
              of the page does, with no separate toggle needed here. */}
          <img src="/brand/zonoo-ai-dark.png" alt="zonoo.ai" className="h-6 w-auto dark:hidden" />
          <img
            src="/brand/zonoo-ai-light.png"
            alt="zonoo.ai"
            className="h-6 w-auto hidden dark:block"
          />
        </div>
        <p className="text-sm text-foreground-muted text-center">{t('Sign in to continue.')}</p>

        <label className="block text-sm">
          <span className="block text-foreground-muted mb-1">{t('Email')}</span>
          <input
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2.5 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </label>

        <label className="block text-sm">
          <span className="block text-foreground-muted mb-1">{t('Password')}</span>
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2.5 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </label>

        {error && (
          <p className="text-sm text-critical" role="alert">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="w-full py-2.5 rounded-lg bg-primary hover:bg-primary-hover text-primary-foreground font-medium transition-colors disabled:opacity-60"
        >
          {busy ? t('Signing in…') : t('Sign in')}
        </button>
      </form>
    </div>
  )
}
